import { Link as RouterLink, useParams, useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { employerVerificationDashboardApi } from '../api/employerVerificationDashboardApi';
import { EmployerInvestigationBody } from '../components/verification/EmployerInvestigationBody';
import { formatVerifiedRelativeRail } from '../lib/formatEmployerVerifiedRelative';
import { messageFromEmployerVerificationHttpError } from '../lib/employerVerificationHttpErrorMessage';
import { finalOutcomeLabelStored } from '../lib/employerVerificationCopy';
import {
  confidenceBucketLabel,
  normalizedConfidenceScore,
} from '../components/verification/EmployerInvestigationGateSupport';

const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

function truncateId(id: string, head = 12, tail = 8): string {
  if (id.length <= head + tail + 1) return id;
  return `${id.slice(0, head)}…${id.slice(-tail)}`;
}

function CopyableIdCaption({
  label,
  value,
  head,
  tail,
  copyAriaLabel,
}: {
  label: string;
  value: string;
  head?: number;
  tail?: number;
  copyAriaLabel: string;
}) {
  const shown =
    head != null && tail != null ? truncateId(value, head, tail) : truncateId(value);
  return (
    <>
      <Tooltip title={value} placement="top" arrow>
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: MONO }}>
          {label} {shown}
        </Typography>
      </Tooltip>
      <IconButton
        size="small"
        aria-label={copyAriaLabel}
        onClick={() => void navigator.clipboard.writeText(value)}
        edge="end"
      >
        <ContentCopyRoundedIcon fontSize="small" />
      </IconButton>
    </>
  );
}

export function EmployerInvestigationWorkspacePage() {
  const { verificationId = '' } = useParams<{ verificationId: string }>();
  const decodedId = decodeURIComponent(verificationId);
  const [searchParams] = useSearchParams();
  const applicationId = searchParams.get('applicationId')?.trim() ?? '';

  const detailQ = useQuery({
    queryKey: ['employerVerificationDetail', decodedId],
    queryFn: () => employerVerificationDashboardApi.detail(decodedId),
    enabled: !!decodedId,
  });

  const dossierQ = useQuery({
    queryKey: ['companyDossier', decodedId],
    queryFn: () => employerVerificationDashboardApi.companyDossier(decodedId),
    enabled: !!decodedId,
  });

  const detail = detailQ.data;
  const dossier = dossierQ.data;

  const backHref = applicationId
    ? `/applications/${encodeURIComponent(applicationId)}`
    : '/employer-verification';

  const employerTitle =
    dossier?.employer?.legalName?.trim() ||
    detail?.employer?.legalName?.trim() ||
    'Employer verification';

  const storedFinal = detail?.finalStatus ?? null;
  const drawerConfidenceRaw =
    typeof detail?.confidenceScore === 'number' && Number.isFinite(detail.confidenceScore)
      ? detail.confidenceScore
      : null;
  const drawerScore01 =
    drawerConfidenceRaw != null ? normalizedConfidenceScore(drawerConfidenceRaw) : null;
  const drawerConfidenceBucket =
    drawerScore01 != null ? confidenceBucketLabel(drawerScore01) : null;

  const headerStatusLine =
    detail != null
      ? [
          finalOutcomeLabelStored(storedFinal),
          drawerConfidenceBucket ? `${drawerConfidenceBucket} confidence` : null,
        ]
          .filter(Boolean)
          .join(' · ')
      : '';

  const verifiedLine =
    detail?.verifiedAt != null
      ? formatVerifiedRelativeRail(String(detail.verifiedAt)) || new Date(detail.verifiedAt).toLocaleString()
      : null;

  if (!decodedId) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="warning">Missing verification id.</Alert>
      </Box>
    );
  }

  const loaded = detail && !detailQ.isPending && !dossierQ.isPending;

  return (
    <Box sx={{ maxWidth: 1280, mx: 'auto', py: { xs: 2, md: 3 }, px: { xs: 1.5, sm: 2 } }}>
      <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ mb: loaded ? 1 : 2 }}>
        <IconButton component={RouterLink} to={backHref} aria-label="Back" size="small" sx={{ mt: 0.25 }}>
          <ArrowBackRoundedIcon />
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.08 }}>
            Investigation workspace
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: -0.02, lineHeight: 1.2 }}>
            {employerTitle}
          </Typography>
          {loaded ? (
            <>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ fontWeight: 700, letterSpacing: 0.06, mt: 1.5, display: 'block' }}
              >
                Global context
              </Typography>
              <Stack spacing={0.75} sx={{ mt: 0.5 }}>
                {headerStatusLine ? (
                  <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary', lineHeight: 1.45 }}>
                    {headerStatusLine}
                  </Typography>
                ) : null}
                {verifiedLine ? (
                  <Tooltip title={detail.verifiedAt ? new Date(detail.verifiedAt).toLocaleString() : ''} placement="top" arrow>
                    <Typography variant="body2" color="text.secondary" component="span">
                      {verifiedLine}
                    </Typography>
                  </Tooltip>
                ) : null}
                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" sx={{ pt: 0.25 }}>
                  <CopyableIdCaption
                    label="Verification ID"
                    value={decodedId}
                    copyAriaLabel="Copy verification ID"
                  />
                  {applicationId ? (
                    <CopyableIdCaption
                      label="App"
                      value={applicationId}
                      head={8}
                      tail={6}
                      copyAriaLabel="Copy application ID"
                    />
                  ) : null}
                  {dossier?.employer?.ceid ? (
                    <CopyableIdCaption
                      label="CEID"
                      value={String(dossier.employer.ceid)}
                      head={10}
                      tail={6}
                      copyAriaLabel="Copy CEID"
                    />
                  ) : null}
                </Stack>
              </Stack>
            </>
          ) : null}
        </Box>
      </Stack>

      {loaded ? <Divider sx={{ my: 2 }} /> : null}

      {detailQ.isError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {messageFromEmployerVerificationHttpError(detailQ.error)}
        </Alert>
      ) : null}
      {dossierQ.isError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {messageFromEmployerVerificationHttpError(dossierQ.error)}
        </Alert>
      ) : null}

      {detailQ.isPending || dossierQ.isPending ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : detail ? (
        <EmployerInvestigationBody detail={detail} dossier={dossier} />
      ) : (
        <Alert severity="warning">Verification record not found.</Alert>
      )}
    </Box>
  );
}
